(ns dnd.util
  (:require
    ["jwt-decode" :rename {jwtDecode jwt-decode'}]
    ["ulid" :rename {ulid ulid'}]
    [re-frame.core :as rf]
    [cljs-uuid-utils.core :as uuid]
    [goog.crypt :as goog.crypt]
    [goog.crypt.Md5]
    [cemerick.url :as url]
    [applied-science.js-interop :as j]

    ;; NOTE - we are using non-public ReactSVGPanZoom functions
    ;;        here - so be **very** careful with updates this dep
    ["@austinbirch/react-svg-pan-zoom/build-commonjs/features/common.js" :as rspz-common]
    ["@austinbirch/react-svg-pan-zoom/build-commonjs/features/zoom.js" :as rspz-zoom]
    [clojure.string :as str]
    ["debounce" :as debounce']
    ["luxon" :refer [DateTime]])
  (:import (goog.async
             Debouncer))
  (:require-macros [dnd.util]))

(def <sub (comp deref rf/subscribe))
(def >evt rf/dispatch)
(def >evt-sync rf/dispatch-sync)

(defn non-empty-string?
  [s]
  (and (string? s)
       (not (str/blank? s))))

(defn contains-many?
  [coll vs]
  {:pre [(coll? coll)
         (coll? vs)]}
  (every? (fn [v]
            (contains? coll v))
          vs))

(defn update-if-exists
  "Like `clojure.core/update` except only runs the update
  function if the key exists in the map."
  [m k f & args]
  (if (contains? m k)
    (apply update m k f args)
    m))

(defn update-in-if-exists
  "Like `clojure.core/update-in` except only runs the
  update function if the nested value exists in the map."
  [m path f & args]
  (if (not= ::not-found (get-in m path ::not-found))
    (apply update-in m path f args)
    m))

(defn error?
  [x]
  (instance? js/Error x))

(defn throw-err
  [x]
  (if (error? x)
    (throw x)
    x))

(defn find-first
  [p coll]
  (first (filter p coll)))

(defn index-of
  [item coll]
  (first (keep-indexed #(if (= item %2) %1) coll)))

(defn update-first
  "Updates the first item in collection `coll` that matches
  the predicate `p` by applying `f` to it.

  If no match, the original collection is returned."
  [p f coll]
  (let [[h t] (split-with (complement p) coll)]
    (if (empty? t)
      coll
      (concat h (list (f (first t))) (rest t)))))

(defn debounce
  "Prevents execution of `f` multiple times during `interval`"
  [f interval]
  (let [dbnc (Debouncer. f interval)]
    (fn [& args]
      (.apply (.-fire dbnc) dbnc (to-array args)))))

(defn debounce-leading
  "Prevents execution of `f` multiple times. Differs from `debounce` in
  that `f` is immediately called, and later calls are ignored (until
  after `interval` has passed)."
  [f interval]
  (debounce' f interval true))

(defn distinct-by
  [f coll]
  (map first (vals (group-by f coll))))

(defn make-uuid
  []
  (uuid/uuid-string (uuid/make-random-squuid)))

(defn deterministic-uuid
  "Generates a deterministic UUID-formatted string from a given input string.
  Same input always produces the same output. Uses MD5 to hash the input
  and formats the result as a UUID v3-style string."
  [input]
  (let [md5 (goog.crypt.Md5.)
        _ (.update md5 (goog.crypt/stringToUtf8ByteArray (str input)))
        hash-bytes (.digest md5)
        hex (apply str (map #(.slice (str "0" (.toString % 16)) -2) hash-bytes))]
    (str (subs hex 0 8) "-"
         (subs hex 8 12) "-"
         (subs hex 12 16) "-"
         (subs hex 16 20) "-"
         (subs hex 20 32))))

(defn account-map-id-fn
  "Deterministic ID generation for account map nodes.
  Ensures the same company/root always gets the same node ID
  regardless of when the graph is built. Link IDs are NOT
  deterministic — they just need to be unique."
  [type & args]
  (condp = type
    :company-node (deterministic-uuid (str "account-map-company-node:" (first args)))
    :root-node (deterministic-uuid (str "account-map-root-node:" (first args)))))

(defn ulid
  []
  (ulid'))

(defonce debug-counters (atom {}))
(defn make-debug-id
  [prefix]
  {:pre [(non-empty-string? prefix)]}
  (let [next-state (swap! debug-counters update
                          prefix (fnil inc 0))]
    (str prefix "-" (get next-state prefix))))

(def jwt-decode jwt-decode')

(def firestore-style-id-charset
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789")

(defn make-firestore-style-id
  []
  (let [bytes (js/Uint8Array. 20)]
    (js/crypto.getRandomValues bytes)
    (apply str
           (map (fn [b]
                  (.charAt firestore-style-id-charset
                           (mod b 62)))
                bytes))))

(defn hash-md5
  [s]
  (let [md5 (goog.crypt.Md5.)]
    (.update md5 (goog.crypt/stringToByteArray s))
    (goog.crypt/byteArrayToHex (.digest md5))))

(defn svg-chart-node->offset-rect
  [chart-node]
  (let [chart-node-x (js/parseInt (j/get-in chart-node [:dataset :xOffset]), 10)
        chart-node-y (js/parseInt (j/get-in chart-node [:dataset :yOffset]), 10)
        chart-node-width (js/parseInt (or (j/get-in chart-node [:dataset :width])
                                          (.getAttribute chart-node "width")) 10)
        chart-node-height (js/parseInt (or (j/get-in chart-node [:dataset :height])
                                           (.getAttribute chart-node "height")) 10)
        node-offset-rect {:top chart-node-y
                          :left chart-node-x
                          :right (+ chart-node-x
                                    chart-node-width)
                          :bottom (+ chart-node-y
                                     chart-node-height)
                          :width chart-node-width
                          :height chart-node-height}]
    node-offset-rect))

(defn outside-hubspot-url
  ([]
   (outside-hubspot-url {}))
  ([{:keys [anchor]}]
   (let [target (-> (url/url (.. js/window -location -href))
                    (assoc-in [:query "outside-hubspot"] true))]
     ;; url is stripping trailing slash, so build manually ourselves
     (str (:protocol target) "://"
          (:host target)
          (url/port-str (:protocol target) (:port target))
          (:path target)
          (when (seq (:query target)) (str \? (if (string? (:query target))
                                                (:query target)
                                                (url/map->query (:query target)))))
          (if anchor
            (str \# anchor)
            (when (:anchor target) (str \# (:anchor target))))))))

(defn update-url-params
  "Takes a URL string, potentially containing query params,
  and applies query param updates according to the hash-map `params`.

  Returns a string representing that URL with the query param
  updates."
  [url params]
  (let [url-obj (js/URL. url)
        search-params (.-searchParams url-obj)]
    (doseq [[k v] params]
      (.set search-params (name k) v))
    (j/assoc! url-obj :search (.toString search-params))
    (.toString url-obj)))

(defn remove-nils
  "Remove nil values from a map.

  https://stackoverflow.com/questions/3937661/remove-nil-values-from-a-map"
  [m]
  (apply dissoc
         m
         (for [[k v] m :when (nil? v)] k)))

(def debug? ^boolean goog.DEBUG)

(defn stringify-keyword
  [kw]
  (if-let [ns (namespace kw)]
    (str ns "/" (name kw))
    (name kw)))

(defn queue-microtask
  [f]
  (if (exists? (.-queueMicrotask js/window))
    (.call (.-queueMicrotask js/window) nil f)
    (-> (.resolve js/Promise)
        (.then f))))

(defn on-next-animation-frame
  ([f]
   (on-next-animation-frame f 1))
  ([f frame-counter]
   {:pre [(pos-int? frame-counter)]}
   (if (< 1 frame-counter)
     (on-next-animation-frame f (dec frame-counter))
     (js/requestAnimationFrame f))))

(defn m-start
  [label]
  (.mark js/performance (str label ":start"))
  nil)

(defn m-end
  [label]
  (.mark js/performance (str label ":end"))
  (.measure js/performance
            label
            (str label ":start")
            (str label ":end"))
  nil)

(defn tippy-reference-client-rect-under-transform
  [el]
  ;; NOTE: for some reason tippy's initial position is based on
  ;; bounding client rect (once), but doesn't account for scaling
  ;; transforms that might be set on parent elements - so returning
  ;; the unscaled size rect of the element (but the transformed
  ;; position) seems to work out
  (let [w (.-offsetWidth el)
        h (.-offsetHeight el)
        rect (.getBoundingClientRect el)
        adjusted-rect (j/assoc! rect
                                :width w
                                :height h
                                :right (+ (.-right rect)
                                          w)
                                :bottom (+ (.-top rect)
                                           h))]
    adjusted-rect))

(defn fit-to-viewer
  ([viewer-value]
   (fit-to-viewer viewer-value
                  {:padding-factor 1
                   :right-spacer-size 0}))
  ([viewer-value {:keys [padding-factor
                         right-spacer-size]}]
   (let [scale-factor-min (j/get viewer-value :scaleFactorMin)
         scale-factor-max (j/get viewer-value :scaleFactorMax)
         viewer-width (j/get viewer-value :viewerWidth)
         viewer-value (if (number? right-spacer-size)
                        (j/extend! #js {}
                                   viewer-value
                                   #js {:viewerWidth (- viewer-width right-spacer-size)})
                        viewer-value)
         without-limits (.setZoomLevels rspz-common viewer-value nil nil)
         fitted (as-> without-limits matrix
                      (.fitToViewer rspz-zoom matrix "center" "center")
                      (.zoomOnViewerCenter rspz-zoom matrix padding-factor))
         ;; if the post-fitted scale factor is out-of-bounds, scale to it
         post-fitted-scale-factor (j/get fitted :a)
         next-scale-factor (if scale-factor-min
                             (js/Math.max post-fitted-scale-factor scale-factor-min)
                             post-fitted-scale-factor)
         next-scale-factor (if scale-factor-max
                             (js/Math.min post-fitted-scale-factor scale-factor-max)
                             next-scale-factor)

         ;; figure out how much we need to scale by to get back in bounds
         scale-factor-diff (when (not= post-fitted-scale-factor
                                       next-scale-factor)
                             (/ next-scale-factor post-fitted-scale-factor))

         rescaled (if scale-factor-diff
                    (.zoomOnViewerCenter rspz-zoom
                                         fitted
                                         scale-factor-diff)
                    fitted)
         with-limits-reset (.setZoomLevels rspz-common
                                           rescaled
                                           scale-factor-min
                                           scale-factor-max)
         with-right-spacer-reset (if (not= viewer-width (j/get with-limits-reset :viewerWidth))
                                   (j/extend! #js {}
                                              with-limits-reset
                                              #js {:viewerWidth viewer-width})
                                   with-limits-reset)]
     with-right-spacer-reset)))


(defn linear-conversion
  [{:keys [value
           old-min old-max
           new-min new-max]}]
  (let [old-range (- old-max old-min)
        new-range (- new-max new-min)]
    (+ (/ (* (- value old-min) new-range)
          old-range)
       new-min)))

;; hardcoded limit, not sure if 16384x16384 is allowed. see https://github.com/puppeteer/puppeteer/issues/359
(def chrome-screenshot-max-size 16384)

(defn scale-factor-for-screenshot
  [{:keys [width height max-width max-height]
    :or {max-width chrome-screenshot-max-size
         max-height chrome-screenshot-max-size}}]
  "Returns the scale factor that needs to be applied
  to `width` and `height` in order for us to take a
  reasonable screenshot via browserless."
  (if (and (<= width max-width) (<= height max-height))
    1
    (let [width-scale-factor (/ max-width width)
          height-scale-factor (/ max-height height)]
      (min width-scale-factor height-scale-factor))))

(defn marketing-site-host
  []
  (let [app-host (.. js/window -location -host)]
    (str/replace app-host "app." "")))

;; taken from https://gist.github.com/YurySolovyov/834e9cd6258f36d6f333fd3d0806c238
(defn class-names [& args]
  (clojure.string/join
    " "
    (mapv name
          (reduce (fn [arr arg]
                    (cond
                      (or (string? arg)
                          (symbol? arg)
                          (keyword? arg)) (conj arr arg)
                      (vector? arg) (vec (concat arr arg))
                      (map? arg) (vec (concat arr
                                              (reduce-kv (fn [map-arr key value]
                                                           (if (true? value)
                                                             (conj map-arr key)
                                                             map-arr)) [] arg)))
                      :else arr)) [] args))))


(defn load-fonts!
  "Forces the browser to start loading the fonts we know
  we are going to use later. It’s a bit of a hack, but if
  we don't do this, the first time we perform text
  measurement on a different font family or weight, we will
  end up measuring the browser default font instead.

  Give this function a canvas context, and for each font
  that we want to load it will write some text to force a
  load."
  [canvas-context]
  (let [fonts ["normal normal 500 20px \"Inter\""
               "normal normal 600 20px \"Inter\""
               "normal normal 700 20px \"Inter\""]]
    (->> fonts
         (map-indexed (fn [i font]
                        (doto canvas-context
                          (j/assoc! :font font)
                          (.fillText "Hello World" 50 (+ 50 (* i 25))))))
         doall)))

(defn remove-hidden-canvas!
  [id]
  (when-let [canvas (.getElementById js/document id)]
    (.removeChild (.-body js/document) canvas)))

(defn create-hidden-canvas!
  [id]
  (remove-hidden-canvas! id)
  (let [canvas (.createElement js/document "canvas")]
    (doto canvas
      (j/assoc! :id id)
      (j/assoc! :width 300)
      (j/assoc! :height 150))
    (j/extend! (j/get canvas :style)
               #js {:position "absolute"
                    :top "-200px"
                    :left "0px"
                    :width "300px"
                    :height "150px"
                    :opacity "0"
                    :background-color "rgba(0,100,255,1)"})
    (.appendChild (.-body js/document) canvas)
    canvas))

(defn redirect!
  [url]
  {:pre [(string? url)]}
  (set! (.-location js/document) url))

(defn iso-string->display-shortdate
  [iso-string]
  (-> (.fromISO DateTime iso-string)
      (.toLocaleString (.-DATE_SHORT DateTime))))

(defn iso-datetime-now
  []
  (.toISO (.utc DateTime)))
